/**
 * Helpers AG-UI + caminho de compatível com payloads legados (event_type/snake_case).
 */

import type { AgentEvent } from '../types'

export type WireAgentEvent = AgentEvent & Record<string, unknown>

export function effectiveEventKind(ev: Partial<AgentEvent>): string {
  const t = ev.type ?? ev.event_type
  return typeof t === 'string' ? t : ''
}

export function effectivePipelineStep(ev: Partial<AgentEvent>): string | undefined {
  const s = ev.stepName ?? ev.step
  return typeof s === 'string' ? s : undefined
}

export function effectiveMessageId(ev: Partial<AgentEvent>): string {
  const m = ev.messageId ?? ev.message_id
  return typeof m === 'string' ? m : ''
}

export function effectiveTextDelta(ev: Partial<AgentEvent>): string {
  const d = ev.delta ?? ev.content
  return typeof d === 'string' ? d : d != null ? String(d) : ''
}

export function effectiveRunId(ev: Partial<AgentEvent>): string | undefined {
  const r = ev.runId ?? ev.run_id
  return typeof r === 'string' ? r : undefined
}

/** Garante `type` preenchido e propaga campos canônicos AG-UI (F0/F4 gradual). **/
export function normalizeWireEvent(raw: unknown): WireAgentEvent | null {
  if (!raw || typeof raw !== 'object') return null
  const base = raw as Record<string, unknown>
  const type =
    typeof base.type === 'string'
      ? base.type
      : typeof base.event_type === 'string'
        ? base.event_type
        : ''

  const stepName =
    typeof base.stepName === 'string'
      ? base.stepName
      : typeof base.step === 'string'
        ? base.step
        : undefined

  const messageId =
    typeof base.messageId === 'string'
      ? base.messageId
      : typeof base.message_id === 'string'
        ? base.message_id
        : undefined

  const runId =
    typeof base.runId === 'string'
      ? base.runId
      : typeof base.run_id === 'string'
        ? base.run_id
        : undefined

  const out = { ...base, type } as WireAgentEvent
  if (stepName) out.stepName = stepName
  if (messageId) out.messageId = messageId
  if (runId) out.runId = runId
  return out.type ? out : null
}

export function normalizeAgentEventsList(rawList: unknown): AgentEvent[] {
  if (!Array.isArray(rawList)) return []
  return rawList.map((r) => normalizeWireEvent(r)).filter(Boolean) as AgentEvent[]
}

export function aggregatedTextMessageBodies(events: AgentEvent[]): Map<string, string> {
  const acc = new Map<string, string>()
  for (const ev of events) {
    const k = effectiveEventKind(ev)
    if (k !== 'TEXT_MESSAGE_CONTENT') continue
    const mid = effectiveMessageId(ev)
    if (!mid) continue
    acc.set(mid, (acc.get(mid) || '') + effectiveTextDelta(ev))
  }
  return acc
}
